import { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
	useNavigate,
	useLocation,
	useSearchParams,
	Link as ReactRouterLink,
} from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Parser from 'react-html-parser';
import { errorMessages, errorMessagesWithPhone } from '../../scripts/languages';
import useSendEnforceMFAOtp from '../../data/queryHooks/useSendEnforceMFAOtp';
import useEnforceMFAOtp from '../../data/queryHooks/useEnforceMFAOtp';
import FormWrapper from '../../shared/FormWrapper';
import { getIsASHLink } from '../../utils/identityHelperFunctions';
import { authenticate } from '../../data/apiCalls';

const validationSchema = yup.object({
	username: yup
		.string('Enter your username')
		.trim('trim empty spaces')
		.required('Please enter a username.'),
	password: yup
		.string('Enter your password')
		.trim('trim empty spaces')
		.required('Please enter a password.'),
});

/**
 * Login component for user authentication. This component will be used in AshLink on public page just as a component and not with the router. For all apps that have dedicated login page should use `identityRoutes` config.
 *
 * @param {Object} props - Component props.
 * @param {string} props.apiEnvironment - Used primarily to determine the correct API endpoint. These should adhere to the [CAST guidelines](https://dev.azure.com/ashcompanies/CAST/_wiki/wikis/CAST.wiki/1758/Standardized-Cross-Team-Variables).
 * @param {string} props.clientId - Client ID used for when hitting the backend. Ex. `empowered-decisions.code`
 * @param {string} props.customGenericErrorMessage - Allows overriding of the default error message shown when login fails but no special case is indicated.
 * @param {string} props.customerServicePhoneNumber - Phone number for customer service where users can call to contact. Ex. `1-877-405-2746`
 * @param {boolean} props.disableWrapperStyle - If you want to remove the default margin, padding, and maxWidth of the component, you can set this to `true`. Default would be `false`.
 * @param {string} props.eligibilityURL - If user is not eligible to register, please provide URL of your website eligibility page. Ex. `/faq`, `/eligibility`
 * @param {boolean} props.enableSSO - Will enable SSO login and registration pages if set to true, otherwise will redirect when people try and visit the page.
 * @param {boolean} props.ineligibleRegistration - If user is not eligible to register, will go to below provided `eligibilityURL` route.
 * @param {string} props.loginHostingPath - If the login page is hosted on a different path, you can provide the path here. Default would be `identity`.
 * @param {string} props.overrideMainTitleTo - If you want to override the main title of the page, you can provide a string here. Default would be `Member Login`.
 * @param {string} props.redirectUri - Callback URL to call after authentication. Ex. `https://dev.empowered-decisions.com/callback`
 */
const Login = ({
	// NOTE: If you update these props, you muse also add them to these 2 files if you want them to be passed into route-based stories:
	// src/components/LoginRouterWrapper/LoginRouterWrapper.jsx
	// src/mocks/StorybookClient.jsx
	apiEnvironment,
	clientId,
	customerServicePhoneNumber,
	customGenericErrorMessage = null,
	disableWrapperStyle = false,
	eligibilityURL = null,
	enableSSO = false,
	ineligibleRegistration = false,
	loginHostingPath = '..',
	overrideMainTitleTo = 'Member Login',
	redirectUri,
}) => {
	if (
		!apiEnvironment ||
		!redirectUri ||
		!clientId ||
		!customerServicePhoneNumber
	) {
		const errorMessage = `[Identity Login] Failed to render; missing required props`;
		console.error(errorMessage);
		throw new Error(errorMessage);
	}

	const [errorHandler, setErrorHandler] = useState({});
	const [mfaOptions, setMfaOptions] = useState([]);
	const [MFAQueryEnabled, setMFAQueryEnabled] = useState(false);
	const [otpMFAOptionsServerError, setOtpMFAOptionsServerError] =
		useState(false);
	const [
		continueToCodeValidationComponentComponent,
		setContinueToCodeValidationComponentComponent,
	] = useState(false);
	const [isFormPosting, setIsFormPosting] = useState(false);
	const [mfaToken, setMfaToken] = useState(null);
	const [enableOTPQuery, setEnableOTPQuery] = useState(false);
	// This needs to be set to empty string to avoid Warning: `value` prop on `input` should not be null.
	const [loginResponseCode, setLoginResponseCode] = useState('');
	const [formAction, setFormAction] = useState(null);

	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const errorLanguages = errorMessagesWithPhone(customerServicePhoneNumber);
	const changedPasswordMessage =
		'Your password has been updated. Please log in with your updated password to access features and services.';
	const isSSO =
		enableSSO &&
		location?.pathname?.includes('/sso') &&
		searchParams.get('token');
	const SSOTooltipMessage =
		'To protect your account from unauthorized access, we ask that you verify your login credentials on this platform so we can link your account to your employer or plan sponsor. You will only need to complete this step once.';
	const enforceHeaders = {
		'Content-Type': 'application/x-www-form-urlencoded',
		'api-version': 3.0,
	};

	const { isError: isOtpSendError } = useSendEnforceMFAOtp(
		apiEnvironment,
		mfaToken,
		mfaOptions?.[0]?.medium,
		enableOTPQuery,
		() => {
			// Turn off this query
			setEnableOTPQuery(false);
			setContinueToCodeValidationComponentComponent(true);
		}
	);

	const onLoginSuccess = (response, values) => {
		const status = response.status?.toLowerCase() ?? '';
		if (status === 'enforcestrongpassword') {
			navigate(`${loginHostingPath}/login/enforcePassword`, {
				state: { token: response.token, username: values.username },
			});
		} else if (status === 'enforcemfaonboard') {
			// We should send the user to the onboarding page and pass along some response values
			const { code, redirectUri, token } = response;
			navigate(`${loginHostingPath}/login/onboarding`, {
				state: { code, redirectUri, token },
			});
		} else if (status === 'enforcesecurityquestion') {
			navigate(`${loginHostingPath}/login/enforceSecurityQuestion`, {
				state: { token: response.token, username: values.username },
			});
		} else if (status === 'enforcemfa') {
			setMFAQueryEnabled(true);
			setMfaToken(response.token);
		} else if (status === 'login') {
			setLoginResponseCode(response.code);
			setFormAction(response.redirectUri);
		}
	};

	useEffect(() => {
		if (loginResponseCode && formAction) {
			setIsFormPosting(true);
			/* This submission using the native form API is required to redirect the user to the
			 correct page which BE handles as part of the authentication process */
			document.getElementById('loginForm').submit();
		}
	}, [loginResponseCode, formAction]);

	/*
	 * This function is used to enforce MFA OTP if the user has MFA enabled.
	 * If the GET request is successful it returns the user's MFA validation options (SMS,email)
	 * */
	useEnforceMFAOtp(apiEnvironment, mfaToken, MFAQueryEnabled, response => {
		if (response.name === 'AxiosError') {
			setOtpMFAOptionsServerError(true);
			return;
		}
		setOtpMFAOptionsServerError(false);
		// Set the user's MFA options
		if (response.mfaOptions) {
			setMfaOptions(response.mfaOptions);
		}

		/* Send the the code to the user's verification method if they only have one verified option.
			If there are two verification methods we don't send the code here because we send them to 
			the screen to select which option they want to choose to have the code sent to
			*/
		if (response.mfaOptions?.length === 1) {
			setEnableOTPQuery(true);
		} else if (
			// We expect the user to have between one and two verified options
			!Array.isArray(response.mfaOptions) ||
			response.mfaOptions?.length < 1 ||
			response.mfaOptions?.length > 2
		) {
			setOtpMFAOptionsServerError(true);
		}
		// Turn off this query
		setMFAQueryEnabled(false);
		if (response.mfaOptions && response.mfaOptions.length === 2) {
			// If the user has two verified options, email and phone,
			const email = response.mfaOptions.find(
				i => i.medium.toLowerCase() === 'email'
			)?.address;
			const phoneNumber = response.mfaOptions.find(
				i => i.medium.toLowerCase() === 'sms'
			)?.address;
			navigate(`${loginHostingPath}/login/mfa`, {
				state: {
					mfaToken,
					email,
					phoneNumber,
					mfaPostOtpLoginValidation: false,
					redirectUri,
					clientId,
				},
			});
		}
	});

	const onLoginError = (error, setFieldError) => {
		const errorCode = error?.response?.data?.responseStatus?.errorCode || '';

		if (errorCode) {
			if (errorCode === 'InvalidLogin') {
				if (customGenericErrorMessage) {
					setErrorHandler({
						type: 'customGenericErrorMessage',
						message: customGenericErrorMessage,
					});
				} else {
					setFieldError('username', errorMessages.invalidUser);
					setFieldError('password', ' ');
				}
			} else if (errorCode === 'LockedOutUser') {
				setErrorHandler({
					type: 'LockedOutUserError',
					message: errorMessages.lockedUser,
				});
				setFieldError('username', ' ');
				setFieldError('password', ' ');
			} else if (errorCode === 'NotEligible') {
				setErrorHandler({
					type: 'NotEligibleError',
					message: errorLanguages.userNotElegible,
				});
			} else if (isSSO) {
				// TODO: Test integrating to an react app and check if we can use navigate('login') instead of below page refresh.
				window.location.href = window.location.href.replace(
					'login/sso',
					'login'
				);
			} else {
				// unrecognized code
				setErrorHandler({
					type: 'GenericError',
					message: errorMessages.generic,
				});
			}
		} else {
			// no code given
			setErrorHandler({
				type: 'GenericError',
				message: errorMessages.generic,
			});
		}
	};

	const { isLoading, mutate: submitForm } = useMutation(
		data => authenticate(apiEnvironment, data.requestData, enforceHeaders),
		{
			onSuccess: (response, data) => {
				onLoginSuccess(response, data.values);
			},
			onError: (error, data) => {
				onLoginError(error, data.setFieldError);
			},
		}
	);

	const handleSubmit = async (values, { setFieldError }) => {
		const requestData = {
			client_id: clientId,
			redirect_uri: redirectUri,
			scope: 'read openid',
			username: values.username,
			password: values.password,
			response_type: 'code',
		};

		if (isSSO && searchParams.get('token')) {
			requestData.token = searchParams.get('token');
		}

		const data = {
			requestData,
			values,
			setFieldError,
		};

		if (isLoading !== true) {
			// clearing errors
			setErrorHandler({});
			setFieldError('username', '');
			setFieldError('password', '');

			submitForm(data);
		}
	};

	const formik = useFormik({
		initialValues: {
			username: '',
			password: '',
		},
		validationSchema,
		onSubmit: handleSubmit,
	});

	useEffect(() => {
		// reroute user to login if invalid SSO login
		if (
			(!isSSO && location?.pathname?.includes('/sso')) ||
			(!searchParams.get('token') && location?.pathname?.includes('/sso'))
		) {
			// TODO: Test integrating to an react app and check if we can use navigate('login') instead of below page refresh.
			window.location.href = window.location.href.replace('login/sso', 'login');
		}

		const errorCode =
			searchParams.get('errorCode') || location?.state?.errorCode;
		if (errorCode) {
			if (errorCode === 'InvalidLogin') {
				if (customGenericErrorMessage) {
					setErrorHandler({
						type: 'customGenericErrorMessage',
						message: customGenericErrorMessage,
					});
				} else {
					formik.setTouched({ username: true, password: true }, false);
					formik.setErrors({
						username: errorMessages.invalidUser,
						password: ' ',
					});
				}
			} else if (errorCode === 'LockedOutUser') {
				setErrorHandler({
					type: 'LockedOutUserError',
					message: errorMessages.lockedUser,
				});
			} else if (errorCode === 'Expired') {
				setErrorHandler({
					type: 'ExpiredError',
					message: errorMessages.expiredSession,
				});
			} else if (errorCode === 'NotEligible') {
				setErrorHandler({
					type: 'NotEligibleError',
					message: errorLanguages.userNotElegible,
				});
			} else {
				// unrecognized code
				setErrorHandler({
					type: 'GenericError',
					message: errorMessages.generic,
				});
			}
		}
	}, []);

	// If the user only has one verified option, either phone or email, go directly to the code validation component
	if (continueToCodeValidationComponentComponent) {
		const { medium, address } = mfaOptions[0];

		// If the one-time code is successfully sent
		navigate(`${loginHostingPath}/login/mfaCodeValidation`, {
			state: {
				mfaToken,
				medium,
				address,
				mfaPostOtpLoginValidation: false,
				isLoginDirectNavigation: true,
				redirectUri,
				clientId,
			},
		});

		/* If the one time code is not sent successfully there will be an OtpSendError alert rendered,
		 which is set in the useSendMFAOtp response and prevents the user from logging in and they remain on the login screen to try again
		*/
	}

	return (
		<FormWrapper
			action={formAction}
			disableWrapperStyle={disableWrapperStyle}
			id="loginForm"
			method="POST"
			onSubmit={formik.handleSubmit}
		>
			<Typography variant="h5" color="primary.main" component="h1" mb={4}>
				{overrideMainTitleTo}
			</Typography>
			{getIsASHLink(clientId) && (
				<Typography mb={4}>
					Log in here if you have an account created{' '}
					<strong>after 03/10/2025</strong>.
				</Typography>
			)}
			{isSSO && (
				<Typography mb={4}>
					Please enter your information below to link your account and continue.
					<Tooltip title={SSOTooltipMessage} arrow>
						<IconButton>
							<InfoOutlinedIcon fontSize="small" color="secondary" />
						</IconButton>
					</Tooltip>
				</Typography>
			)}
			{searchParams.get('message') && (
				<Box mb={2}>
					<Alert severity="success" data-testid="ChangePasswordMessage">
						{changedPasswordMessage}
					</Alert>
				</Box>
			)}
			{errorHandler.message && (
				<Box mb={2}>
					<Alert severity="error" data-testid={errorHandler.type}>
						{Parser(errorHandler.message)}
					</Alert>
				</Box>
			)}
			<input type="hidden" name="code" value={loginResponseCode} />
			<input
				type="hidden"
				name="continue"
				value={searchParams.get('redirect') || ''}
			/>
			<input type="hidden" value="login" name="type" />
			{isOtpSendError && (
				<Box mb={2}>
					<Alert severity="error">
						Uh oh, one-time code could not be sent. Please try again.
					</Alert>
				</Box>
			)}
			{otpMFAOptionsServerError && (
				<Box mb={2}>
					<Alert severity="error">
						Uh oh, unexpected server error. Please try again.
					</Alert>
				</Box>
			)}
			<Stack spacing={3}>
				<TextField
					id="username"
					name="username"
					label="Username"
					InputLabelProps={{
						shrink: true,
					}}
					defaultValue={formik.values.username}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.username && Boolean(formik.errors.username)}
					helperText={formik.touched.username && formik.errors.username}
				/>
				<TextField
					type="password"
					id="password"
					name="password"
					label="Password"
					InputLabelProps={{
						shrink: true,
					}}
					value={formik.values.password}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.password && Boolean(formik.errors.password)}
					helperText={formik.touched.password && formik.errors.password}
				/>
				<Button
					size="large"
					type="submit"
					variant="contained"
					color="secondary"
					fullWidth
					disabled={isLoading || isFormPosting}
				>
					{isLoading || isFormPosting ? 'Loading...' : 'Login'}
				</Button>
			</Stack>
			<Typography textAlign="center" mt={2}>
				Forgot your{' '}
				<Link
					component={ReactRouterLink}
					to={`${loginHostingPath}/forgotUsername`}
					color="secondary"
					underline="none"
				>
					Username
				</Link>{' '}
				or{' '}
				<Link
					component={ReactRouterLink}
					to={`${loginHostingPath}/forgotPassword`}
					color="secondary"
					underline="none"
				>
					Password
				</Link>
				?
			</Typography>
			<Divider>
				<Typography variant="body2" my={3}>
					OR
				</Typography>
			</Divider>
			<Typography textAlign="center">
				{getIsASHLink(clientId) ? (
					<Button
						component={ReactRouterLink}
						fullWidth
						to={`${loginHostingPath}/select-role`}
						variant="outlined"
					>
						Create Account
					</Button>
				) : (
					<>
						Not registered?{' '}
						<Link
							component={ReactRouterLink}
							to={
								ineligibleRegistration && eligibilityURL
									? eligibilityURL
									: `${loginHostingPath}/registration`
							}
							color="secondary"
							underline="none"
						>
							Click here
						</Link>{' '}
						to find out how.
					</>
				)}
			</Typography>
		</FormWrapper>
	);
};
Login.propTypes = {
	apiEnvironment: PropTypes.string.isRequired,
	clientId: PropTypes.string.isRequired,
	customerServicePhoneNumber: PropTypes.string.isRequired,
	customGenericErrorMessage: PropTypes.string,
	disableWrapperStyle: PropTypes.bool,
	eligibilityURL: PropTypes.string,
	enableSSO: PropTypes.bool,
	ineligibleRegistration: PropTypes.bool,
	loginHostingPath: PropTypes.string,
	overrideMainTitleTo: PropTypes.string,
	redirectUri: PropTypes.string.isRequired,
};

export default Login;
